import React, { useState, useEffect } from 'react';
import { api } from "@/lib/api";
import { Badge } from "@/components/ui/badge";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { CheckCircle2, Circle, AlertCircle } from 'lucide-react';

/**
 * Operator Status Indicator
 * 
 * Shows completion status of daily/weekly/monthly checklists
 * in a compact format for the War Room header.
 */
const OperatorStatusIndicator = ({ 
  onNavigateToOperator, // Callback to navigate to Operator tab
  className = "" 
}) => {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchStatus = async () => {
      try {
        const res = await api.get('/operator/checklists/status');
        setStatus(res.data);
      } catch (err) {
        console.error('Failed to fetch operator status:', err);
      } finally {
        setLoading(false);
      }
    };
    
    fetchStatus();
    
    // Refresh status every 5 minutes
    const interval = setInterval(fetchStatus, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, []);

  if (loading || !status) {
    return null;
  }

  const getStatusIcon = (isComplete) => {
    return isComplete 
      ? <CheckCircle2 className="h-3 w-3 text-emerald-400" />
      : <Circle className="h-3 w-3 text-slate-500" />;
  };

  const allComplete = status.daily_complete && status.weekly_complete && status.monthly_complete;
  const noneComplete = !status.daily_complete && !status.weekly_complete && !status.monthly_complete;

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <Badge
            variant="outline"
            className={`cursor-pointer transition-colors ${
              allComplete 
                ? 'bg-emerald-500/20 text-emerald-300 border-emerald-500/30 hover:bg-emerald-500/30'
                : noneComplete
                ? 'bg-slate-500/20 text-slate-400 border-slate-500/30 hover:bg-slate-500/30'
                : 'bg-amber-500/20 text-amber-300 border-amber-500/30 hover:bg-amber-500/30'
            } ${className}`}
            onClick={onNavigateToOperator}
            data-testid="operator-status-indicator"
          >
            <div className="flex items-center gap-1.5">
              {allComplete ? (
                <CheckCircle2 className="h-3 w-3" />
              ) : noneComplete ? (
                <AlertCircle className="h-3 w-3" />
              ) : (
                <Circle className="h-3 w-3" />
              )}
              <span className="text-xs">Operator</span>
              <div className="flex items-center gap-0.5 ml-1">
                {getStatusIcon(status.daily_complete)}
                {getStatusIcon(status.weekly_complete)}
                {getStatusIcon(status.monthly_complete)}
              </div>
            </div>
          </Badge>
        </TooltipTrigger>
        <TooltipContent side="bottom" className="bg-slate-900 border-slate-700">
          <div className="text-xs space-y-1">
            <div className="flex items-center gap-2">
              {getStatusIcon(status.daily_complete)}
              <span>Daily: {status.daily_complete ? 'Complete' : 'Incomplete'}</span>
            </div>
            <div className="flex items-center gap-2">
              {getStatusIcon(status.weekly_complete)}
              <span>Weekly: {status.weekly_complete ? 'Complete' : 'Incomplete'}</span>
            </div>
            <div className="flex items-center gap-2">
              {getStatusIcon(status.monthly_complete)}
              <span>Monthly: {status.monthly_complete ? 'Complete' : 'Incomplete'}</span>
            </div>
            <div className="text-slate-500 pt-1 border-t border-slate-700 mt-1">
              Click to open Operator tab
            </div>
          </div>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};

export default OperatorStatusIndicator;
